import { useEffect } from "react";
import "./App.css";
import { Toaster } from "react-hot-toast";
import { ToastProvider } from "./components/ui/Toast";
import { initTokenRefresh } from "./auth/tokenRefresh";

import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import AdminDashboard from "./pages/AdminDashboard/AdminDashboard";
import ManufacturerDashboard from "./pages/ManufacturerDashboard/ManufacturerDashboard";
import Brands from "./pages/Brands/Brands";
import Engagements from "./pages/Engagements/Engagements";
import Wallets from "./pages/Wallets/Wallets";
import PaymentReceipts from "./pages/PaymentReceipts/PaymentReceipts";
import Requirements from "./pages/Requirements/Requirements";
import Fabrics from "./pages/Fabrics/Fabrics";
import Designs from "./pages/Designs/Designs";
import TaskBoard from "./pages/TaskBoard/TaskBoard";
import Orders from "./pages/Orders/Orders";
import Documents from "./pages/Documents/Documents";
import Notifications from "./pages/Notifications/Notifications";
import Reports from "./pages/Reports/Reports";
import Users from "./pages/Users/Users";
import AuditLog from "./pages/AuditLog/AuditLog";
import ManufacturerSettings from "./pages/Settings/ManufacturerSettings";

import BrandDashboardLayout from "./pages/BrandPages/BrandDashboardLayout";
import BrandDashboardIndex from "./pages/BrandPages/BrandDashboardIndex";
import BrandManufacturers from "./pages/BrandPages/BrandManufacturers";
import BrandEngagements from "./pages/BrandPages/BrandEngagements";
import BrandWallet from "./pages/BrandPages/BrandWallet";
import BrandReceipts from "./pages/BrandPages/BrandReceipts";
import BrandRequirements from "./pages/BrandPages/BrandRequirements";
import BrandFabrics from "./pages/BrandPages/BrandFabrics";
import BrandDesigns from "./pages/BrandPages/BrandDesigns";
import BrandBoard from "./pages/BrandPages/BrandBoard";
import BrandOrders from "./pages/BrandPages/BrandOrders";
import BrandDocuments from "./pages/BrandPages/BrandDocuments";
import BrandNotifications from "./pages/BrandPages/BrandNotifications";
import BrandReports from "./pages/BrandPages/BrandReports";
import BrandTeam from "./pages/BrandPages/BrandTeam";
import BrandAudit from "./pages/BrandPages/BrandAudit";
import BrandSettings from "./pages/BrandPages/BrandSettings";

import Login from "./pages/Login/Login";
import LoginPassword from "./pages/LoginPassword/LoginPassword";
import LoginForgotPassword from "./pages/LoginForgotPassword/LoginForgotPassword";
import LoginOtp from "./pages/LoginOtp/LoginOtp";
import SetPassword from "./pages/SetPassword/SetPassword";
import Signup from "./pages/Signup/Signup";
import { LoginEntry, LoginMethodRoute, SignupRoute } from "./auth/ConfiguredLogin";
import Callback from "./pages/Callback/Callback";
import SplashScreen from "./pages/SplashScreen/SplashScreen";
import ProtectedRoute from "./auth/ProtectedRoute";
import { SetupProvider } from "./auth/SetupContext";
import CreateClient from "./pages/Setup/CreateClient/CreateClient";
import CreateCompany from "./pages/Setup/CreateCompany/CreateCompany";
import GrantAccess from "./pages/Setup/GrantAccess/GrantAccess";
import CreateRoles from "./pages/Setup/CreateRoles/CreateRoles";
import RegisterUser from "./pages/Setup/RegisterUser/RegisterUser";
import GrantUserAccess from "./pages/Setup/GrantUserAccess/GrantUserAccess";

function App() {
  useEffect(() => {
    initTokenRefresh();
  }, []);

  return (
    <>
      <Toaster
        position="bottom-right"
        containerStyle={{ zIndex: 1000002 }}
        toastOptions={{ duration: 3000 }}
      />
      <BrowserRouter>
        <ToastProvider>
          <SetupProvider>
            <Routes>
              <Route path="/" element={<LoginEntry />} />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="/login/sso" element={<LoginMethodRoute method="sso"><Login /></LoginMethodRoute>} />
              <Route path="/login/password" element={<LoginMethodRoute method="password"><LoginPassword /></LoginMethodRoute>} />
              <Route path="/login/password/forgot" element={<LoginMethodRoute method="password"><LoginForgotPassword /></LoginMethodRoute>} />
              <Route path="/login/otp" element={<LoginMethodRoute method="otp"><LoginOtp /></LoginMethodRoute>} />
              <Route path="/login/set-password/:token" element={<SetPassword />} />
              <Route path="/signup" element={<SignupRoute><Signup /></SignupRoute>} />
              <Route path="/callback" element={<Callback />} />
              <Route path="/splash" element={<ProtectedRoute><SplashScreen /></ProtectedRoute>} />
              <Route path="/setup/client" element={<CreateClient />} />
              <Route path="/setup/company" element={<CreateCompany />} />
              <Route path="/setup/access" element={<GrantAccess />} />
              <Route path="/setup/roles" element={<CreateRoles />} />
              <Route path="/setup/user" element={<RegisterUser />} />
              <Route path="/setup/user-access" element={<GrantUserAccess />} />

              {/* Manufacturer Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              >
                <Route index element={<ManufacturerDashboard />} />
                <Route path="brands" element={<Brands />} />
                <Route path="engagements" element={<Engagements />} />
                <Route path="wallets" element={<Wallets />} />
                <Route path="receipts" element={<PaymentReceipts />} />
                <Route path="requirements" element={<Requirements />} />
                <Route path="fabrics" element={<Fabrics />} />
                <Route path="designs" element={<Designs />} />
                <Route path="board" element={<TaskBoard />} />
                <Route path="orders" element={<Orders />} />
                <Route path="documents" element={<Documents />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="reports" element={<Reports />} />
                <Route path="users" element={<Users />} />
                <Route path="audit" element={<AuditLog />} />
                <Route path="settings" element={<ManufacturerSettings />} />
              </Route>

              {/* Brand Web App Routes */}
              <Route
                path="/brand"
                element={
                  <ProtectedRoute>
                    <BrandDashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<BrandDashboardIndex />} />
                <Route path="manufacturers" element={<BrandManufacturers />} />
                <Route path="engagements" element={<BrandEngagements />} />
                <Route path="wallet" element={<BrandWallet />} />
                <Route path="receipts" element={<BrandReceipts />} />
                <Route path="requirements" element={<BrandRequirements />} />
                <Route path="fabrics" element={<BrandFabrics />} />
                <Route path="designs" element={<BrandDesigns />} />
                <Route path="board" element={<BrandBoard />} />
                <Route path="orders" element={<BrandOrders />} />
                <Route path="documents" element={<BrandDocuments />} />
                <Route path="notifications" element={<BrandNotifications />} />
                <Route path="reports" element={<BrandReports />} />
                <Route path="team" element={<BrandTeam />} />
                <Route path="audit" element={<BrandAudit />} />
                <Route path="settings" element={<BrandSettings />} />
              </Route>
            </Routes>
          </SetupProvider>
        </ToastProvider>
      </BrowserRouter>
    </>
  );
}

export default App;
